import { motion } from "framer-motion";

/**
 * Signature korv. matte-glass ritual jar.
 *
 * A stubby, weighted vessel: wide flat lid, softly rounded body, etched wordmark.
 * Two size variants — "signature" (50ml) and "travel" (15ml).
 * Fully SVG. Soft-touch matte shading — no hard specular highlight.
 */
export default function KorvJar({
  color = "#1a1512",        // jar glass tone (dark by default — a "zen" object)
  lidColor,                 // optional — defaults to slightly darker than body
  accent = "#a08258",       // etched wordmark colour
  label = "korv.",
  sublabel = "ENCENS · 50 ML",
  variant = "signature",    // "signature" | "travel"
  className = "",
  animate = true,
}) {
  const lid = lidColor || shade(color, -14);
  const uid = (color + accent + variant).replace(/[^a-z0-9]/gi, "");
  const isTravel = variant === "travel";

  // Body geometry — travel is proportionally shorter but same width feeling
  const bodyH = isTravel ? 110 : 170;   // pixels of body height
  const lidH  = isTravel ? 36  : 48;
  const bodyW = isTravel ? 190 : 240;
  const totalH = lidH + bodyH + 12;    // + contact shadow allowance
  const totalW = 300;

  const bx = (totalW - bodyW) / 2;
  const lidW = bodyW - 14;
  const lx  = (totalW - lidW) / 2;

  const bodyTop = lidH + 8;
  const bodyBottom = bodyTop + bodyH;

  return (
    <motion.svg
      viewBox={`0 0 ${totalW} ${totalH + 30}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      initial={animate ? { y: 24, opacity: 0 } : false}
      whileInView={animate ? { y: 0, opacity: 1 } : false}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.6, ease: [0.2, 0.7, 0.1, 1] }}
      aria-hidden="true"
    >
      <defs>
        {/* Ambient ground shadow */}
        <radialGradient id={`ground-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.42)" />
          <stop offset="0.6" stopColor="rgba(20,18,17,0.14)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <filter id={`sblur-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" />
        </filter>

        {/* Body — matte glass */}
        <linearGradient id={`body-${uid}`} x1="0.05" y1="0" x2="0.98" y2="0">
          <stop offset="0"    stopColor="rgba(255,255,255,0.00)" />
          <stop offset="0.05" stopColor="rgba(255,255,255,0.16)" />
          <stop offset="0.22" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="0.55" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.82" stopColor="rgba(0,0,0,0.16)" />
          <stop offset="1"    stopColor="rgba(0,0,0,0.34)" />
        </linearGradient>
        <linearGradient id={`bodyV-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="rgba(0,0,0,0.28)" />
          <stop offset="0.12" stopColor="rgba(0,0,0,0.04)" />
          <stop offset="0.55" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.85" stopColor="rgba(0,0,0,0.10)" />
          <stop offset="1"    stopColor="rgba(0,0,0,0.32)" />
        </linearGradient>

        {/* Lid */}
        <linearGradient id={`lid-${uid}`} x1="0.05" y1="0" x2="0.98" y2="0">
          <stop offset="0"    stopColor="rgba(255,255,255,0.22)" />
          <stop offset="0.2"  stopColor="rgba(255,255,255,0.06)" />
          <stop offset="0.55" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.85" stopColor="rgba(0,0,0,0.24)" />
          <stop offset="1"    stopColor="rgba(0,0,0,0.42)" />
        </linearGradient>
        <linearGradient id={`lidTop-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="0.4" stopColor="rgba(255,255,255,0.06)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        {/* Etched wordmark glow — subtle inset */}
        <filter id={`etch-${uid}`}>
          <feGaussianBlur stdDeviation="0.4" />
        </filter>

        <clipPath id={`bodyClip-${uid}`}>
          <rect x={bx} y={bodyTop} width={bodyW} height={bodyH} rx="6" />
        </clipPath>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={totalW / 2} cy={bodyBottom + 14} rx={bodyW * 0.55} ry={9}
        fill={`url(#ground-${uid})`} filter={`url(#sblur-${uid})`} />
      <ellipse cx={totalW / 2} cy={bodyBottom + 10} rx={bodyW * 0.4} ry={4}
        fill="rgba(20,18,17,0.25)" />

      {/* ─── LID ─── */}
      <g>
        {/* Cast shadow of lid on body */}
        <rect x={bx} y={bodyTop} width={bodyW} height={4} fill="rgba(0,0,0,0.25)" />
        {/* Lid main */}
        <rect x={lx} y={4} width={lidW} height={lidH} rx="3" fill={lid} />
        <rect x={lx} y={4} width={lidW} height={lidH} rx="3" fill={`url(#lid-${uid})`} />
        {/* Top-face highlight — signals leak-proof seal */}
        <rect x={lx + 4} y={6} width={lidW - 8} height={7} rx="2" fill={`url(#lidTop-${uid})`} />
        {/* Lid rim edge */}
        <rect x={lx} y={4} width={lidW} height={lidH} rx="3" fill="none"
          stroke="rgba(0,0,0,0.18)" strokeWidth="0.6" />
        {/* Undercut groove between lid and body */}
        <rect x={lx + 2} y={lidH + 2} width={lidW - 4} height={2} fill="rgba(0,0,0,0.35)" />
        <rect x={lx + 2} y={lidH + 4} width={lidW - 4} height={2} fill="rgba(255,255,255,0.05)" />
      </g>

      {/* ─── BODY ─── */}
      <g>
        <rect x={bx} y={bodyTop} width={bodyW} height={bodyH} rx="6" fill={color} />
        <g clipPath={`url(#bodyClip-${uid})`}>
          <rect x={0} y={bodyTop} width={totalW} height={bodyH} fill={`url(#body-${uid})`} />
          <rect x={0} y={bodyTop} width={totalW} height={bodyH} fill={`url(#bodyV-${uid})`} />
        </g>
        {/* fine outline */}
        <rect x={bx} y={bodyTop} width={bodyW} height={bodyH} rx="6" fill="none"
          stroke="rgba(0,0,0,0.20)" strokeWidth="0.6" />

        {/* Etched korv. wordmark — pressed into the glass */}
        <text
          x={totalW / 2}
          y={bodyTop + bodyH / 2 + 4}
          textAnchor="middle"
          fill={accent}
          fillOpacity="0.9"
          fontFamily="Fraunces, 'Cormorant Garamond', serif"
          fontWeight="400"
          fontSize={isTravel ? 26 : 34}
          letterSpacing="-1"
          style={{ fontVariationSettings: "'opsz' 144" }}
          filter={`url(#etch-${uid})`}
        >
          {label}
        </text>

        {/* Sublabel band */}
        <text
          x={totalW / 2}
          y={bodyBottom - 14}
          textAnchor="middle"
          fill={accent}
          fillOpacity="0.55"
          fontFamily="'Inter Tight', sans-serif"
          fontWeight="400"
          fontSize={isTravel ? 6.5 : 7.5}
          letterSpacing={isTravel ? 1.8 : 2.2}
        >
          {sublabel}
        </text>
      </g>
    </motion.svg>
  );
}

/* darken (or lighten) a hex colour by percent (-100..100) */
function shade(hex, percent) {
  const c = hex.replace("#", "");
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const f = percent / 100;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * f)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * f)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * f)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}
